import { inject, Injectable } from '@angular/core';
import { EntityState } from '@ngrx/entity';

import { applyPropsPatch, CanvasElement, CanvasElementId } from '@blaze-runtime/engine-domain';
import { StyleSheet } from '@blaze-shared/theming-domain';
import { getValueByPath, setValueByPath } from '@blaze-shared/workflow-domain';

import { isElementType } from '../../../canvas-elements/element-model/element-type-guards';
import { getPathLastInSlot } from '../../../utils/layout/helpers';
import { placeElementMutably } from '../../../utils/layout/place-element';
import { ElementDefinitionsService } from '../../element-definitions.service';
import { makeStyleConfigPropsForDefaultStyleSheet } from '../../styles';

import { type FormLikeElement } from './form-generation-data';

/** Labels the generation is allowed to replace, because the author did not choose them. */
const GENERATED_COMMIT_LABELS = ['Add', 'Edit', 'Save'];

/** Creates the button that commits a form, and keeps its label in step with the action. */
@Injectable({ providedIn: 'root' })
export class FormCommitButtonGenerationService {
  private readonly elementDefinitions = inject(ElementDefinitionsService);

  /** Adds or updates the commit button */
  addOrUpdateCommitButtonMutably(
    entityState: EntityState<CanvasElement>,
    styleSheets: StyleSheet[],
    form: FormLikeElement,
    buttonId: CanvasElementId | undefined,
    commitLabel: string | undefined,
  ): { commitButtonId: CanvasElementId } {
    if (buttonId) {
      this.updateCommitLabelMutably(entityState, buttonId, commitLabel);
      return { commitButtonId: buttonId };
    }

    const elementDefinition = this.elementDefinitions.getDefinitionSafe('button');
    elementDefinition.name = 'Commit button';

    const baseProps = applyPropsPatch<'button'>(elementDefinition.initial ?? {}, {
      content: {
        button: { label: commitLabel || 'Save' },
      },
      ...makeStyleConfigPropsForDefaultStyleSheet(styleSheets, elementDefinition.type),
    });

    const initProps = this.elementDefinitions
      .getPropsBuilder('button', baseProps)
      ?.setMargin('$t.spacingFormField')
      .build();

    const result = placeElementMutably(entityState, {
      elementDefinition,
      atPath: getPathLastInSlot(entityState, form.id, '0'),
      initProps,
    });
    if (!result?.elementId) {
      throw new Error('Error when placing commit button');
    }

    return { commitButtonId: result.elementId };
  }

  private updateCommitLabelMutably(
    entityState: EntityState<CanvasElement>,
    buttonId: CanvasElementId,
    commitLabel: string | undefined,
  ): void {
    const button = entityState.entities[buttonId];
    if (!button || !isElementType(button, 'button')) {
      throw new Error('Element error');
    }
    if (!commitLabel) {
      return;
    }

    const currentLabel = getValueByPath<string>(button.props, 'content.button.label');
    // why: a label the author typed is theirs to keep; only a generated one is replaced
    if (
      !currentLabel ||
      (currentLabel !== commitLabel && GENERATED_COMMIT_LABELS.includes(currentLabel))
    ) {
      setValueByPath(button.props, 'content.button.label', commitLabel);
    }
  }
}
